import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench, faChessBoard } from '@fortawesome/free-solid-svg-icons';
import AppContext from '../context/AppContext';
import { useAppDispatch, useAppSelector } from '../store';
import { closeRepair, setGameIdForModal } from '../reducers/uiReducer';
import { REPAIR_DEPTHS } from '../types/CorrectionModel';
import WalletMirror from './WalletMirror';
import RepairSession from './RepairSession';
import { getIcon, moveTypeClass } from '../utils/ChessTypes';
import GamesAPI from '../api/GamesAPI';

// The cross-game repair session, as a full-screen overlay. Repairing your own mistakes is what this
// site is for, so the session gets the same room as the game overlay (size xl, min(1280px, 96vw),
// fullscreen on mobile) rather than the cramped dialog it used to open in — the board, the reveal and
// the panel beside it all need space at once.
//
// Scope comes from the store: an empty id list means "everything matching the current filter" (the
// toolbar's Repair button), a populated one narrows it to those games (the list's per-game repair
// pill). Depth is picked in this header and the session below re-keys on it.
//
// The unnarrowed scope asks the server for every matching id rather than reading the page in the
// store. The list is paged at ten, and a repair session drawn from ten games isn't cross-game
// practice — it's whatever happened to be on screen. The page's own ids stand in while the full set
// is in flight, so the session starts immediately and widens a moment later.
// The most games one session will draw from, matching CorrectionsController's own cap.
const SCOPE_CAP = 3000;

export default function RepairModal(): React.ReactNode {
  const dispatch = useAppDispatch();
  const { isMobile } = React.useContext(AppContext);
  const repairGameIds = useAppSelector((state) => state.uiReducer.repairGameIds);
  const repairMinWeight = useAppSelector((state) => state.uiReducer.repairMinWeight);
  const { params } = useAppSelector((state) => state.paramsReducer);
  const { games } = useAppSelector((state) => state.gamesReducer);
  const [minWeight, setMinWeight] = useState(4);

  // Every session starts at blunders: the worst mistakes are the ones worth a session, and a depth
  // carried over from last time would silently change what you're practising. The exception is a
  // caller that has already counted what's in scope and knows blunders would come up empty (/try,
  // working from two games) — it names the depth to open at.
  useEffect(() => {
    if (repairGameIds != null) {
      setMinWeight(repairMinWeight ?? 4);
    }
  }, [repairGameIds, repairMinWeight]);

  const list = games?.List ?? [];
  const pageIds = useMemo(() => {
    if (repairGameIds == null || repairGameIds.length > 0) return [];
    // Only games the engine has looked at can offer a mistake to repair.
    return list.filter((g) => g.LastEvaluation != null).map((g) => g.Id);
  }, [repairGameIds, list]);

  // Every game the current filter matches, fetched once per opening of the modal. Null until it
  // lands, which is what the "Finding your games…" spinner below waits on: the session is keyed on
  // its scope, so starting on the page's ten and widening a second later would restart a session the
  // player had already begun. On failure it falls back to the page rather than staying empty.
  const [filterIds, setFilterIds] = useState<number[] | null>(null);
  const narrowed = (repairGameIds?.length ?? 0) > 0;
  useEffect(() => {
    if (repairGameIds == null || narrowed) {
      setFilterIds(null);
      return;
    }
    let cancelled = false;
    GamesAPI.getGameIds(params, true)
      .then((ids) => {
        // Matches the server's own cap on a queue request. The ids come back newest first, so a
        // library bigger than this practises its most recent few thousand games rather than an
        // arbitrary slice — and the request stays a scan rather than a download.
        if (!cancelled) setFilterIds(ids.length > 0 ? ids.slice(0, SCOPE_CAP) : pageIds);
      })
      .catch(() => {
        if (!cancelled) setFilterIds(pageIds);
      });
    return () => {
      cancelled = true;
    };
    // The filter object is rebuilt each render, so key on what it actually says.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairGameIds, narrowed, JSON.stringify(params)]);

  const gameIds = useMemo(() => {
    if (repairGameIds == null) return [];
    if (repairGameIds.length > 0) return repairGameIds;
    return filterIds ?? [];
  }, [repairGameIds, filterIds]);

  if (repairGameIds == null) {
    return null;
  }

  const close = () => dispatch(closeRepair());
  const depthLabel =
    REPAIR_DEPTHS.find((d) => d.minWeight === minWeight)?.label ?? REPAIR_DEPTHS[0].label;

  return (
    <Modal
      size="xl"
      show={true}
      onHide={close}
      dialogClassName="cm-repair-modal"
      scrollable={!isMobile}
      fullscreen={isMobile ? true : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex flex-fill align-items-center">
          <span className="cm-repair-modal-title">
            <FontAwesomeIcon icon={faWrench} className="me-2" />
            Fix Mistakes Across Your Games
          </span>
          <span className="cm-repair-modal-scope text-muted">
            {narrowed
              ? `${gameIds.length === 1 ? 'this game' : `${gameIds.length} games`}`
              : `${gameIds.length} ${gameIds.length === 1 ? 'game' : 'games'}`}
            {' · '}
          </span>
          {/* Depth reads as a quiet inline link beside the scope, not a button: it's a setting you
              change occasionally, and an accent button up here both shouted and cost a line of
              vertical space on a phone — where the board is the thing that needs it. */}
          <Dropdown align="end" className="cm-repair-modal-depth">
            <Dropdown.Toggle variant="link" className="cm-repair-depth-link" id="repair-depth">
              <FontAwesomeIcon icon={faChessBoard} className="me-1" />
              {depthLabel}
            </Dropdown.Toggle>
            <Dropdown.Menu className="cm-repair-menu">
              <Dropdown.Header>How deep to repair</Dropdown.Header>
              {/* The same tinted rows as the game overlay's severity picker, with the icon of the
                  severity each depth adds. Radios, not checkboxes: the depths nest, so you pick one
                  rather than combining them. */}
              {REPAIR_DEPTHS.map((d) => {
                const Icon = getIcon(d.moveType);
                const on = d.minWeight === minWeight;
                return (
                  <label key={d.minWeight} className={`cm-repair-cat${on ? ' is-on' : ''}`}>
                    <input
                      type="radio"
                      name="repair-depth"
                      checked={on}
                      onChange={() => setMinWeight(d.minWeight)}
                    />
                    <span className={`cm-repair-cat-icon ${moveTypeClass(d.moveType)}`}>
                      <Icon />
                    </span>
                    <span className="cm-repair-cat-label">{d.label}</span>
                  </label>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
          {/* The overlay covers the header, so the money needs a face in here — otherwise the
              payout for the repair you just made flies to a counter you can't see. */}
          <WalletMirror />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {gameIds.length === 0 ? (
          <div className="cm-repair-empty">
            <Spinner animation="border" size="sm" className="me-2" />
            Finding your games…
          </div>
        ) : (
          // Keyed on scope and depth so changing either starts a clean session rather than leaving
          // the old queue's position, hint level and board behind.
          <RepairSession
            key={`${minWeight}:${gameIds.length}:${gameIds[0] ?? 0}:${gameIds[gameIds.length - 1] ?? 0}`}
            gameIds={gameIds}
            username={params.username}
            minWeight={minWeight}
            onExit={close}
            // The session and the game are both overlays, so opening the game closes this one; the
            // queue rebuilds from the same scope when you come back.
            onOpenGame={(gameId) => {
              close();
              dispatch(setGameIdForModal(gameId));
            }}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}
